import { AsyncBoundary, Button, Col, Row } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue } from 'jotai';
import { LucideHistory } from 'lucide-react';

import * as govReportingState from '../../_state/govReporting.state';
import { useSettingsLocation } from '../../_state/settingsModule.state';
import { useTranslations } from '../../i18n/useTranslations';
import { SettingsSubmodule } from '../_layouts/SettingsSubmodule';
import { SettingsContent } from '../_shared/SettingsContent';

import { FacilityAssignmentFormDialog } from './FacilityAssignmentFormDialog';
import { FacilityAssignmentPanel } from './FacilityAssignmentPanel';
import { FieldSelectionPanel } from './FieldSelectionPanel';

import { DetailField } from '~/shared/components/DetailField';
import { formatZonedDateTime } from '~/shared/utils/dateTime';

export const GovReportingConfigDetailView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const configQuery = useAtomValue(
    govReportingState.$currentGovReportingConfig,
  );
  const config = configQuery.data;

  const dayLabels = t.complianceView.govReporting.detail.dayLabels;
  const daysOfWeek = config?.daysOfWeek as Record<string, boolean> | null;
  const enabledDays = daysOfWeek
    ? Object.entries(daysOfWeek)
        .filter(([, enabled]) => enabled)
        .map(([day]) => dayLabels[day as keyof typeof dayLabels] ?? day)
        .join(', ')
    : '–';

  return (
    <Col className="@GovReportingConfigDetailView h-full">
      <AsyncBoundary
        loading={configQuery.isLoading}
        error={configQuery.error}
        className="h-full"
      >
        {!config ? (
          <Col className="h-full items-center justify-center">
            <div className="text-neutral/50">
              {t.complianceView.govReporting.detail.notFound}
            </div>
          </Col>
        ) : (
          <SettingsSubmodule
            title={config.name}
            subtitle={t.complianceView.govReporting.detail.subtitle}
            tabs={null}
            onBackPressed={() => setLocation('/compliance/reporting')}
          >
            <SettingsContent size="medium">
              <Col gap6>
                <Col gap4 className="rounded-lg border border-neutral/10 p-5">
                  <div className="text-sm font-semibold">
                    {t.complianceView.govReporting.detail.sections.configInfo}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <DetailField
                      label={
                        t.complianceView.govReporting.detail.fields.serviceType
                      }
                      value={config.serviceType}
                    />
                    <DetailField
                      label={
                        t.complianceView.govReporting.detail.fields.uploadType
                      }
                      value={config.uploadType}
                    />
                    <DetailField
                      label={
                        t.complianceView.govReporting.detail.fields.recyclerId
                      }
                      value={config.recyclerId}
                    />
                    <DetailField
                      label={t.complianceView.govReporting.detail.fields.email}
                      value={config.toEmailAddress}
                    />
                    <DetailField
                      label={
                        t.complianceView.govReporting.detail.fields.timezone
                      }
                      value={config.uploadTimezone}
                    />
                    <DetailField
                      label={
                        t.complianceView.govReporting.detail.fields
                          .batchProcessing
                      }
                      value={
                        config.processBatchesAsUnit
                          ? t.complianceView.common.yes
                          : t.complianceView.common.no
                      }
                    />
                    <DetailField
                      label={t.complianceView.govReporting.detail.fields.active}
                      value={
                        <span
                          className={cn(
                            config.isActive && 'text-success',
                            !config.isActive && 'text-neutral/40',
                          )}
                        >
                          {config.isActive
                            ? t.complianceView.common.yes
                            : t.complianceView.common.no}
                        </span>
                      }
                    />
                  </div>
                </Col>

                <Col gap4 className="rounded-lg border border-neutral/10 p-5">
                  <div className="text-sm font-semibold">
                    {t.complianceView.govReporting.detail.sections.schedule}
                  </div>
                  <Row gap4>
                    <DetailField
                      label={
                        t.complianceView.govReporting.detail.fields.daysOfWeek
                      }
                      value={enabledDays}
                    />
                  </Row>
                </Col>

                <FacilityAssignmentPanel />
                <FacilityAssignmentFormDialog />
                <FieldSelectionPanel />

                <div className="grid grid-cols-2 gap-4 text-xs text-neutral/40">
                  <DetailField
                    label={t.complianceView.govReporting.detail.fields.created}
                    value={formatZonedDateTime(config.createdAt, {
                      includeTime: true,
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  />
                  <DetailField
                    label={t.complianceView.govReporting.detail.fields.updated}
                    value={formatZonedDateTime(config.updatedAt, {
                      includeTime: true,
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  />
                </div>
                <Button
                  variant="outline"
                  color="neutral"
                  icon={<LucideHistory />}
                  onPress={() => setLocation('/compliance/dispatch-history')}
                >
                  {
                    t.complianceView.govReporting.detail.actions
                      .viewDispatchHistory
                  }
                </Button>
              </Col>
            </SettingsContent>
          </SettingsSubmodule>
        )}
      </AsyncBoundary>
    </Col>
  );
};
